<template>
    <div>
        <div class="tw-flex tw-justify-center">
            <div class="tw-w-full md:tw-w-2/3">
                <h6 class="tw-text-white tw-font-bold">{{ __('purchase.business_Details_title') }}</h6>
            </div>
        </div>

        <form @submit.prevent="formWillSubmit">
            <div class="tw-flex tw-justify-center">
                <div class="tw-w-full md:tw-w-2/3">
                    <h3 class="tw-text-white">{{ __('purchase.business_details_title') }}</h3>
                    <input 
                        type="text"
                        class="tw-w-full tw-px-3 tw-py-2 tw-border tw-text-white tw-border-gray-300 tw-bg-transparent tw-rounded tw-mt-4"
                        :placeholder="__('purchase.business_name')"
                        v-model="businessName"
                        :class="{ 'tw-border-red-500': showBusinessNameError }"
                        @blur="() => { fieldTouched.business_name = true; }"
                        @input="() => { if (businessName && businessName.length > 0) fieldTouched.business_name = true; }"
                    />
                    <span v-show="showBusinessNameError" class="tw-text-red-600">{{ errors.business_name }}</span>
                </div>
            </div>
            <div class="tw-flex tw-justify-center">
                <div class="tw-w-full md:tw-w-2/3">
                    <select 
                        class="tw-w-full tw-px-3 tw-py-2 tw-border tw-text-white tw-border-white tw-bg-transparent tw-rounded tw-mt-5 tw-mb-4" 
                        v-model="industry" 
                        :class="{ 'tw-border-red-500': showIndustryError }"
                        @blur="() => { fieldTouched.industry = true; }"
                        @change="() => { fieldTouched.industry = true; }"
                    >
                        <option class="tw-bg-gray-800" disabled value="">{{ __('purchase.choose_industry_placeholder') }}</option>
                        <option class="tw-bg-gray-800" v-for="(industryItem, index) in industries" :key="index" :value="index">{{ __('industry')[index] }}</option>
                    </select>
                    <span v-show="showIndustryError" class="tw-text-red-600">{{ errors.industry }}</span>
                </div>
            </div>
        </form>

    </div>

</template>

<script setup lang="ts">
import { reactive, onMounted, getCurrentInstance, watch, computed } from 'vue';
import { useForm, useField } from 'vee-validate';
import * as yup from 'yup';

const instance = getCurrentInstance();
const __ = instance?.appContext.config.globalProperties.__;

interface BusinessDetailsData {
    businessName: string;
    industry: string;
}

interface Props {
    currentBusinessDetails?: BusinessDetailsData;
}

const props = withDefaults(defineProps<Props>(), {
    currentBusinessDetails: () => ({
        businessName: "",
        industry: ""
    })
});

const emit = defineEmits<{
    'business-details-did-submit': [businessDetails: BusinessDetailsData];
}>();

// Business details reactive data - keeping exact same structure
const businessDetails = reactive<BusinessDetailsData>({
    businessName: "",
    industry: ""
});

// Industries data
const industries = window.s.industries;

// Validation schema
const validationSchema = yup.object({
    business_name: yup.string().required(__('purchase.validation.required', { field: __('purchase.field_names.business_name') })),
    industry: yup.string().required(__('purchase.validation.required', { field: __('purchase.field_names.industry') }))
});

// Setup form validation
const { handleSubmit, errors } = useForm({
    validationSchema,
    validateOnMount: false,
});

// Use field for each form field
const { value: businessName } = useField("business_name");
const { value: industry } = useField("industry");

// Track if fields have been touched/interacted with
const fieldTouched = reactive({
    business_name: false,
    industry: false
});

// Computed properties for error display - only show errors after field has been touched
const showBusinessNameError = computed(() => 
    fieldTouched.business_name && !!errors.value.business_name
);
const showIndustryError = computed(() => 
    fieldTouched.industry && !!errors.value.industry
);

// Watch form fields and update businessDetails object
watch(() => businessName.value, (newValue) => {
    businessDetails.businessName = (newValue as string) || "";
});

watch(() => industry.value, (newValue) => {
    businessDetails.industry = (newValue as string) || "";
});

// Helper function to assign JSON object (keeping original functionality)
const assignJSONObject = (source: any, target: any) => {
    if (source && typeof source === 'object') {
        Object.keys(source).forEach(key => {
            if (source[key] !== undefined && source[key] !== null) {
                target[key] = source[key];
            }
        });
    }
};

// Initialize on mount - keeping exact same logic
onMounted(() => {
    assignJSONObject(props.currentBusinessDetails, businessDetails);
    
    // Set form field values from businessDetails
    businessName.value = businessDetails.businessName;
    industry.value = businessDetails.industry;
});

// Form submission - keeping exact same functionality
const formWillSubmit = handleSubmit(async (values) => {
    // Update businessDetails with form values
    businessDetails.businessName = values.business_name;
    businessDetails.industry = values.industry;
    
    emit("business-details-did-submit", businessDetails);
}, (errors) => {
    // Mark all fields as touched so errors show when validation fails
    fieldTouched.business_name = true;
    fieldTouched.industry = true;
    
    // Handle validation errors - keeping exact same scroll behavior
    const errorElement = document.querySelector(".tw-border-red-500");
    if (errorElement) {
        errorElement.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }
});

// Expose methods for parent component
defineExpose({
    formWillSubmit
});
</script>

